// src/locales/English/en.accountSupport.ts

const accountSupport = {
  accountSupport: {
    meta: {
      title: "Account Guide",
      subtitle:
        "Find help for account access, settings, privacy, security, and support.",
      back: "Back",
      overviewTitle: "Help & Support",
      searchPlaceholder: "Search Account Guide"
    },

    sidebar: {  
      accountOverview: "Account Overview",
      registration: "Registration",
      login: "Login & Access",
      myProfile: "My Profile",
      settings: "Account Settings",
      dataPrivacy: "Data & Privacy",
      security: "Security",
      profileManagement: "Profile Management",
      support: "Support & Troubleshooting",
    },

    overview: {
      whatIsMyAccount: "Account Overview",
      profileBasics: "Profile basics",
      accountStatus: "Account status",
      accountVerification: "Account verification",

      registerNewAccount: "How to register a new account",
      registrationEmailMissing: "I did not receive the registration email",
      registrationProblems: "Registration problems",
      usernameAlreadyUsed: "Profile Name already in use",
      emailAlreadyUsed: "Email already in use",
      minimumAgeRequirements: "Minimum age requirements",

      login: "How to log in",
      forgotPassword: "Forgot password",
      resetPassword: "Change password",
      cannotAccessAccount: "Cannot access my account",
      accountLocked: "Account locked",
      verificationCodeIssues: "Verification code issues",
      loginNewDevice: "Login from a new device",
      suspiciousLoginActivity: "Suspicious login activity",

      findingMyProfile: "Where are MyProfile details?",
      profileName: "Profile name",
      changeProfilePicture: "Change your profile picture",
      editBio: "Edit your bio",

      settingsOverview: "Account Settings",
      shareProfileName: "Share your profile name",
      changeEmail: "Change your email address",
      changePassword: "Change your password",
      selectLanguage: "Select your language",
      notificationSettings: "Notification settings",
      manageConnectedAccounts: "Manage connected accounts",
      logoutAllDevices: "Logout from all devices",

      accountManagementOverview: "Account Management Overview",
      deactivateProfile: "Deactivate your profile",
      reactivateProfile: "Reactivate your profile",
      deleteAccount: "Delete your account",
      accountDeletionProcess: "What happens when an account is deleted?",
      dataRetention: "Data retention",
      downloadAccountData: "Download your account data",

      dataPrivacyOverview: "Data & Privacy overview",
      adjustPrivacySettings: "Adjust your privacy settings",
      firstPrivacyChoice: "First privacy choice after registration",
      whoCanSeeProfile: "Who can see my profile?",
      whoCanContactMe: "Who can contact me?",
      blockUser: "Block a user",
      reportUser: "Report a user",
      
 
      securityOverview: "Security overview",
      twoFactorAuthentication: "Two-factor authentication (2FA)",
      how2faWorks: "How 2FA works",
      disableTwoFactorAuthentication: "Disable two-factor authentication",
      keepingAccountSecure: "Keeping your account secure",


      profileManagementOverview: "Profile Management overview",
      myFollowers: "My Followers",
      myFollowees: "My Followees",

      contactSupport: "Contact support",
      reportIssue: "Report a technical issue",
      websiteIssues: "Website issues",
      accountRecovery: "Account recovery",
      appealRestriction: "Appeal a restriction",
      faq: "Frequently asked questions",
    },

    sections: {

/////////////////////////////////
/////////Account Overview////////
/////////////////////////////////

      whatIsMyAccount: {
  title: "Account Overview",
  body: [
    {
      type: "paragraph",
      text: "Your account is your personal access point to the platform. It contains your profile information, login credentials, privacy preferences, security settings, and activity across the platform.",
    },
    {
      type: "bullets",
      items: [
        "Use your account to access your profile and platform features.",
        "Privately chat with other users.",
        "Manage your profile picture, bio (optional), and other profile information.",
        "Follow other profiles.",
        "Adjust privacy, notification, and security settings.",
      ],
    },
  ],
},

      profileBasics: {
        title: "Profile basics",
        body: [
          {
            type: "paragraph",
            text: "Your profile is how your account appears to you and, depending on your privacy settings, to other users.",
          },
          {
            type: "bullets",
            items: [
              "Your profile includes a unique Profile Name. It can also include a profile picture, bio, and other visible information.",
              "You can update most profile details in My Profile. Your Profile Name cannot be changed after registration.",
              "You can deactivate your profile in Account Settings. When your profile is deactivated, your profile and posts are hidden from other users.",
            ],
          },
        ],
      },

/////////////////////////////////
///////////Registration//////////
/////////////////////////////////
      
      registerNewAccount: {
        title: "How to register a new account",
        body: [
        {
         type: "paragraph",
         text: "To create an account, you must be at least 18 years old and have access to a valid email address. Follow the steps below to complete your registration and gain access to the platform.",
        },
       {
        type: "steps",
        items: [
          "Enter a unique Profile Name.",
          "Enter a valid email address.",
          "Create a secure password containing at least 8 characters and one number.",
          "Confirm that you are at least 18 years old by selecting the corresponding checkbox.",
          "Click the 'Create Account' button.",
          "A verification email will be sent from register@elnydo.com.",
          "Open the email and click the verification link.",
          "After successful verification, choose your privacy preferences.",
          "You will then be redirected to the Gallery feed."
          ],
         },
         {
           type: "note",
           text: "Only individuals who are 18 years of age or older may create an account. If you do not receive the registration email within a few minutes, please check your spam or junk folder before requesting a new verification email.",
          },
        ],
      },

      registrationEmailMissing: {
        title: "I did not receive the registration email",
        body: [
          {
            type: "paragraph",
            text: "If your registration email does not arrive, it may have been delayed, blocked, or filtered by your email provider.",
          },
          {
            type: "steps",
            items: [
              "Check your spam, junk, promotions, or other filtered folders.",
              "Make sure you entered the correct email address.",
              "Try a different email address or contact support if the issue persists.",
            ],
          },
          {
            type: "note",
            text: "Never share registration links, login codes, or verification emails with anyone.",
          },
        ],
      },

      registrationProblems: {
        title: "Registration issues",
        body: [
          {
            type: "paragraph",
            text: "Registration may fail if required information is missing, invalid, already in use, or temporarily unavailable.",
          },
          {
            type: "steps",
            items: [
              "Check that all required fields are completed.",
              "Make sure your Profile Name and email address are valid and not already in use.",
              "Try refreshing the page or restarting the app.",
              "Disable browser extensions that may block forms.",
              "Contact support if the issue continues.",
            ],
          },
        ],
      },

      usernameAlreadyUsed: {
        title: "Profile Name already in use",
        body: [
          {
            type: "paragraph",
            text: "If a Profile Name is already in use, another account has already registered it or the Profile Name is unavailable.",
          },
          {
            type: "bullets",
            items: [
              "Try a different Profile Name.",
              "Add numbers, initials, or another variation.",
              "Avoid impersonating another person, brand, or organization.",
            ],
          },
        ],
      },

      emailAlreadyUsed: {
        title: "Email already in use",
        body: [
          {
            type: "paragraph",
            text: "If your email address is already in use, it may already be connected to an existing account.",
          },
          {
            type: "steps",
            items: [
              "Try logging in with that email address.",
              "Use the password reset option if you forgot your password.",
              "Contact support if you believe the email is being used without permission.",
            ],
          },
        ],
      },

      minimumAgeRequirements: {
  title: "Minimum age requirements",
  body: [
    {
      type: "paragraph",
      text: "You must be at least 18 years old to create and maintain an account on this platform.",
    },
    {
      type: "bullets",
      items: [
        "Only individuals who are 18 years of age or older may create an account.",
        "By creating an account, you confirm that you meet the minimum age requirement.",
        "Accounts created by individuals under the age of 18 may be removed.",
      ],
    },
  ],
},

/////////////////////////////////
//////////Login & Access/////////
/////////////////////////////////

      login: {
        title: "How to log in",
        body: [
          {
            type: "paragraph",
            text: "Use your registered login method to access your account.",
          },
          {
            type: "steps",
            items: [
              "Go to the Login page on the Elnydo website. In the mobile app, the Login screen opens automatically when you start the app.",
              "Enter the email address associated with your account and your password, then click the Login button.",
              "If your email address and password are correct, you will be logged in and redirected to the Gallery feed.",
            ],
          },
        ],
      },

      forgotPassword: {
        title: "Forgot password",
        body: [
          {
            type: "paragraph",
            text: "If you forgot your password, use the password reset option from the login screen.",
          },
          {
            type: "steps",
            items: [
              "Select the Forgot Password link on the login page.",
              "Enter the email address connected to your account.",
              "Check your email inbox and spam folder.",
              "Follow the reset link and create a new password.",
            ],
          },
        ],
      },

      resetPassword: {
        title: "Change password",
        body: [
          {
            type: "paragraph",
            text: "Resetting your password helps restore access and protect your account if you cannot log in or suspect unauthorized access.",
          },
          {
            type: "steps",
            items: [
              "Click on Change Password in Account Settings. Alternatively you can use the Forgot Password function in the login page.",
              "To change your password, you must first enter your current password.",
              "Enter a new password and confirm the new password. Passwords must contain at least 8 characters and one number.",
              "Click Confirm to save your new password and continue using your account."
            ],
          },
        ],
      },

      cannotAccessAccount: {
        title: "Cannot access my account",
        body: [
          {
            type: "paragraph",
            text: "If you cannot access your account, the issue may be related to login details, verification, account status, or security restrictions.",
          },
          {
            type: "steps",
            items: [
              "Check that your email address and password are correct.",
              "Try resetting your password using the Forgot Password link.",
              "Check whether you received an account notification or password resetting email.",
              "Contact support if you still cannot access the account.",
            ],
          },
        ],
      },

      accountLocked: {
        title: "Account locked",
        body: [
          {
            type: "paragraph",
            text: "An account may be locked after suspicious activity, repeated failed login attempts, or security concerns.",
          },
          {
            type: "steps",
            items: [
              "Wait before trying again if too many login attempts were made.",
              "Reset your password if you think your account was accessed by someone else.",
              "Follow any instructions shown on the login screen.",
              "Contact support if the account remains locked.",
            ],
          },
        ],
      },

      verificationCodeIssues: {
        title: "Verification code issues",
        body: [
          {
            type: "paragraph",
            text: "Verification codes may fail if they are expired, entered incorrectly, or requested too many times.",
          },
          {
            type: "bullets",
            items: [
              "Enter the newest code you received.",
              "Check that there are no spaces or typing mistakes.",
              "Wait before requesting another code.",
            ],
          },
        ],
      },

      loginNewDevice: {
        title: "Login from a new device",
        body: [
          {
            type: "paragraph",
            text: "When logging in from a new device, additional verification may be required to protect your account.",
          },
          {
            type: "bullets",
            items: [
              "Confirm that the login attempt was made by you.",
              "Complete any required verification steps.",
              "Do not approve login attempts you do not recognize.",
              "Change your password if you notice unfamiliar activity.",
            ],
          },
        ],
      },

      suspiciousLoginActivity: {
        title: "Suspicious login activity",
        body: [
          {
            type: "paragraph",
            text: "Suspicious login activity may indicate that someone else tried to access your account.",
          },
          {
            type: "bullets",
            items: [
              "Change your password immediately.",
              "Review active sessions or connected devices if available.",
              "Enable two-factor authentication.",
              "Contact support if you believe your account is compromised.",
            ],
          },
        ],
      },


   //////////////////// 
      //MyProfile
      ////////////////////

 findingMyProfile: {
    title: "Where are My Profile details?",
      body: [
        {
          type: "paragraph",
          text: "My Profile is your personal area on the platform where you can manage your profile information, review your activity, and access profile-related features.",
        },
        {
          type: "steps",
          items: [
             "On the website, you can find My Profile in the left sidebar.",
             "In the mobile app, you can find My Profile in the menu.",
             "From there, you can manage your profile information and review your activity.",
           ],
        },
        {
          type: "bullets",
          items: [
            "Upload or change your Profile Picture.",
            "Edit your Bio.",
            "Add or change your Location.",
            "View your Posts.",
            "View your Likes.",
            "View your Followers and Following.",
          ],
        },
{
type: "note",
text: "Information displayed on your profile may be visible to other users depending on your privacy settings.",
},
],
},

profileName: {
title: "Profile Name",
body: [
{
type: "paragraph",
text: "Your Profile Name is the public name displayed on your profile and alongside your posts throughout the platform.",
},
{
type: "bullets",
items: [
"Each Profile Name must be unique.",
"Your Profile Name is selected during registration.",
"Other users can use your Profile Name to find and identify your profile.",
"Profile Names cannot be changed after registration.",
],
},
{
type: "note",
text: "Choose your Profile Name carefully during registration, as it cannot be modified later.",
},
],
},

changeProfilePicture: {
title: "Change your profile picture",
body: [
{
type: "paragraph",
text: "Your profile picture helps other users recognize your account and appears on your profile and throughout the platform.",
},
{
type: "steps",
items: [
"Open My Profile.",
"Select Edit Profile.",
"Choose a new image.",
"Preview your profile picture.",
"Save your changes.",
],
},
],
},

editBio: {
title: "Edit your bio",
body: [
{
type: "paragraph",
text: "Your bio is a short description that appears on your profile and can help other users learn more about you.",
},
{
type: "steps",
items: [
"Open My Profile.",
"Select Edit Profile.",
"Update your bio text.",
"Save your changes.",
],
},
{
type: "note",
text: "Avoid sharing sensitive personal information in your bio.",
},
],
},


////////////////////
//Account Settings
////////////////////

settingsOverview: {
  title: "Account Settings Overview",
  body: [
    {
      type: "paragraph",
      text: "Settings is the area where you manage important account preferences and access options.",
    },
    {
      type: "steps",
      items: [
        "On the website, open Settings from the left sidebar. In the mobile app, open Settings from the menu.",
        "Select Account Settings in the Settings overview or sidebar.",
      ],
    },
    {
      type: "bullets",
      items: [
        "Share your Profile Name.",
        "Change your email address.",
        "Change your password.",
        "Select your language.",
        "Manage notification settings.",
        "Deactivate or reactivate your profile.",
      ],
    },
  ],
},


      shareProfileName: {
        title: "Share your Profile Name",
        body: [
          {
            type: "paragraph",
            text: "Your Profile Name is the name other users may use to find or recognize your account.",
          },
          {
            type: "bullets",
            items: [
              "Open your Account Settings in Settings overview or sidebar.",
              "Your Profile Name is displayed at the top of the Account Settings page.",
              "Enter the recipient's email address to share your Profile Name.",
              "Share it only with people you want to connect with.",
              "Review privacy settings if you do not want your profile to be widely visible.",
            ],
          },
        ],
      },

      changeEmail: {
        title: "Change your email address",
        body: [
          {
            type: "paragraph",
            text: "You can change the email address connected to your account in Account Settings.",
          },
          {
            type: "bullets",
            items: [
              "Open Account Settings in Settings overview or sidebar.",
              "Enter your current email address and then your new email address.",
              "Select Change E-mail.",
              "Confirm the change using the 6-digit verification code sent to your new email address.",
            ],
          },
        ],
      },

      changePassword: {
        title: "Change your password",
        body: [
          {
            type: "paragraph",
            text: "Changing your password regularly can help keep your account secure.",
          },
          {
            type: "bullets",
            items: [
              "Open Account Settings in Settings overview or sidebar.",
              "Select Change Password.",
              "Enter your current password and click Continue.",
              "Create a new strong password and confirm it.",
              "Save the change and log in again if required.",
            ],
          },
        ],
      },



  selectLanguage: {
        title: "Select your language",
        body: [
          {
            type: "paragraph",
            text: "Language settings control the language used for menus, labels, messages, and help content where supported.",
          },
          {
            type: "bullets",
            items: [
              "Open Account Settings in Settings overview or sidebar.",
              "Select Language.",
              "Choose your preferred language.",
              "You can also change the language on the website, login page, and registration page.",
            ],
          },
        ],
      },

      notificationSettings: {
        title: "Notification settings",
        body: [
          {
            type: "paragraph",
            text: "Notification settings help you control which updates and alerts you receive.",
          },
          {
            type: "bullets",
            items: [
              "Open Account Settings in Settings overview or sidebar.",
              "Select Notifications.",
              "Turn notification types on or off.",
              "Check device notification permissions if notifications do not appear.",
            ],
          },
        ],
      },


      deactivateProfile: {
        title: "Deactivate your profile",
        body: [
          {
            type: "paragraph",
            text: "Deactivating your profile hides your profile and posts from other users without permanently deleting your account.",
          },
          {
            type: "steps",
            items: [
              "Open Account Settings in Settings overview or sidebar.",
              "Select Deactivate Profile at the bottom of the page.",
              "Enter your current account password and click Deactivate.",
              "If the password is correct, your profile will be deactivated and no longer publicly visible.",
            ],
          },
          {
      type: "note",
      text: "You can reactivate your profile at any time by returning to Account Settings and selecting Reactivate Profile.",
    },
        ],
      },

      reactivateProfile: {
        title: "Reactivate your profile",
        body: [
          {
            type: "paragraph",
            text: "If your profile is deactivated, you can reactivate it any time.",
          },
          {
            type: "steps",
            items: [
              "Open Account Settings in Settings overview or sidebar.",
              "Select Reactivate Profile at the bottom of the page.",
              "Your profile will be reactivated and your posts will become visible again.",
            ],
          },
        ],
      },


   

      ////////////////////
      //Data & Privacy
      ////////////////////

  dataPrivacyOverview: {
  title: "Data & Privacy overview",
  body: [
    {
      type: "paragraph",
      text: "Data & Privacy helps you understand and control privacy choices, account data, deletion, retention, and data access options.",
    },
    {
      type: "bullets",
      items: [
        "Review your first privacy choices after registration.",
        "Adjust privacy settings for visibility and interactions.",
        "Understand what happens after account deletion.",
        "Review data retention and data download options.",
      ],
    },
  ],
},



      whoCanSeeProfile: {
        title: "Who can see my profile?",
        body: [
          {
            type: "paragraph",
            text: "Profile visibility depends on your privacy settings and how the platform displays user profiles.",
          },
          {
            type: "bullets",
            items: [
              "Public profiles may be visible to other users.",
              "Some profile details may appear with posts, messages, or interactions.",
              "Review privacy settings to control visibility where available.",
            ],
          },
        ],
      },


adjustPrivacySettings: {
  title: "Adjust your privacy settings",
  body: [
    {
      type: "paragraph",
      text: "Privacy settings let you update your consent choices for location-based features, analytics, and push notifications.",
    },
    {
      type: "bullets",
      items: [
        "Open Data & Privacy in Settings.",
        "Review your location, analytics, and push notification preferences.",
        "Turn each option on or off.",
        "Save your changes before leaving the page.",
      ],
    },
  ],
},

      firstPrivacyChoice: {
  title: "First privacy choice after registration",
  body: [
    {
      type: "paragraph",
      text: "After registration, you may be asked to review your privacy preferences before entering the platform for the first time.",
    },
    {
      type: "bullets",
      items: [
        "Location consent controls whether location-based features can be used.",
        "Analytics consent controls whether usage analytics may be collected.",
        "Push consent controls whether you may receive notifications.",
      ],
    },
    {
      type: "note",
      text: "You can continue with all options turned off. These choices can be changed later in Data & Privacy settings.",
    },
  ],
},


      deleteAccount: {
        title: "Delete your account",
        body: [
          {
            type: "paragraph",
            text: "Deleting your account permanently removes access to your account and removes or anonymizes account-related information.",
          },
          {
            type: "bullets",
           items: [
  "Open Account Management.",
  "Select Delete Account.",
  "Enter your current password to request deletion.",
  "Check your email for the 6-digit deletion code.",
  "Enter the code to confirm deletion.",
  "Confirm only if you understand that deletion may be permanent.",
],
          },
          {
            type: "note",
            text: "Some information may remain for legal, security, moderation, backup, or operational reasons.",
          },
        ],
      },

      accountDeletionProcess: {
        title: "What happens when an account is deleted?",
        body: [
          {
            type: "paragraph",
            text: "When an account is deleted, access to the account is removed and profile-related information may be deleted, anonymized, or disconnected from the account.",
          },
          {
            type: "bullets",
            items: [
  "You lose access to your account, profile, and settings.",
  "Your posts, drafts, music tracks, notifications, follows, and uploaded media may be deleted.",
  "Messages involving other users may remain visible to those users, but your sender details may be anonymized.",
  "Some records or backups may remain for legal, security, moderation, backup, or operational reasons.",
],
          },
        ],
      },

      dataRetention: {
        title: "Data retention",
        body: [
          {
            type: "paragraph",
            text: "Data retention means how long account-related information is kept before it is deleted, anonymized, or overwritten.",
          },
          {
            type: "bullets",
            items: [
              "Account data may be kept while your account is active.",
              "Some records may be retained after deletion for security or legal reasons.",
              "Backup copies may remain for a limited period.",
              "Retention periods may depend on the type of information.",
            ],
          },
        ],
      },

     downloadAccountData: {
  title: "Download your account data",
  body: [
    {
      type: "paragraph",
      text: "Downloading your account data lets you receive a copy of certain information connected to your account.",
    },
    {
      type: "bullets",
      items: [
        "Open Data & Privacy in Settings.",
        "Select Download Your Data.",
        "Follow the request process.",
        "Wait for the export file to be prepared if required.",
      ],
    },
  ],
},


//////////////////////////////////
/////////////Security/////////////
//////////////////////////////////
      
    securityOverview: {
  title: "Security overview",
  body: [
    {
      type: "paragraph",
      text: "Security settings help protect your account from unauthorized access and suspicious activity.",
    },
    {
      type: "bullets",
      items: [
        "Use a strong password.",
        "Enable two-factor authentication.",
        "Review suspicious login activity.",
        "Log out from all devices if needed.",
        "Never share passwords, reset links, or verification codes.",
      ],
    },
  ],
},

  

twoFactorAuthentication: {
  title: "Two-factor authentication (2FA)",
  body: [
    {
      type: "paragraph",
      text: "Two-factor authentication adds an extra layer of protection by requiring a 6-digit email verification code when logging in.",
    },
    {
      type: "steps",
      items: [
        "Open Security in Settings overview or sidebar.",
        "Select Two-factor authentication.",
        "Enter your current password to request setup.",
        "Check your email for the 6-digit setup code.",
        "Enter the code to enable 2FA.",
        "Save the recovery codes shown after setup.",
      ],
    },
    {
      type: "note",
      text: "Recovery codes are shown only once. Store them safely and do not share them with anyone.",
    },
  ],
},

  how2faWorks: {
  title: "How 2FA works",
  body: [
    {
      type: "paragraph",
      text: "2FA helps confirm that the person logging in is really you, even if someone else knows your password.",
    },
    {
      type: "bullets",
      items: [
        "You enter your email address and password as usual.",
        "If 2FA is enabled, a 6-digit code is sent to your configured email address.",
        "You must enter the code before login is completed.",
        "If the code is expired or incorrect, login will not continue.",
      ],
    },
    {
      type: "note",
      text: "Keep recovery codes private. Anyone with your recovery codes may be able to access your account.",
    },
  ],
},


disableTwoFactorAuthentication: {
  title: "Disable two-factor authentication",
  body: [
    {
      type: "paragraph",
      text: "You can disable two-factor authentication (2FA) if you no longer want to use an additional verification step when logging in.",
    },
    {
      type: "steps",
      items: [
        "Open Settings and select Security from the Settings sidebar.",
        "Select Disable Two-factor Authentication.",
        "Enter your current password.",
        "Confirm that you want to disable 2FA.",
        "After confirmation, 2FA will be disabled for your account.",
      ],
    },
    {
      type: "note",
      text: "Disabling 2FA reduces account security. Anyone with access to your email address and password may be able to access your account.",
    },
  ],
},

      keepingAccountSecure: {
        title: "Keeping your account secure",
        body: [
          {
            type: "paragraph",
            text: "Good security habits help protect your account from unauthorized access.",
          },
          {
            type: "bullets",
            items: [
              "Use a strong and unique password.",
              "Enable 2FA if available.",
              "Do not share login codes or reset links.",
              "Log out from shared devices.",
              "Watch for phishing messages or fake login pages.",
            ],
          },
        ],
      },


logoutAllDevices: {
  title: "Logout from all devices",
  body: [
    {
      type: "paragraph",
      text: "Logging out from all devices revokes active sessions and helps protect your account if you used a shared device or suspect unauthorized access.",
    },
    {
      type: "steps",
      items: [
        "Open Security in Settings overview or sidebar.",
        "Select Logout from all devices.",
        "Confirm the action if requested.",
        "Log in again on devices you want to continue using.",
        "Change your password afterwards if you suspect unauthorized access.",
      ],
    },
  ],
},











      ////////////////////////
      //Profile Management
      ///////////////////////

      profileManagementOverview: {
  title: "Profile Management overview",
  body: [
    {
      type: "paragraph",
      text: "Profile Management helps you control interactions with other users and review profile-related activity.",
    },
    {
      type: "bullets",
      items: [
        "Block users if you do not want them to interact with you.",
        "Report users who violate platform rules.",
        "Review your followers.",
        "Review the users you follow.",
        "Use privacy and security settings together with profile management tools.",
      ],
    },
  ],
},

      blockUser: {
        title: "Block a user",
        body: [
          {
            type: "paragraph",
            text: "Blocking a user can limit their ability to contact you or interact with your profile.",
          },
          {
            type: "bullets",
            items: [
              "Open the user's profile or interaction menu.",
              "Select Block if available.",
              "Confirm the action.",
              "You can review blocked users in privacy settings if supported.",
            ],
          },
        ],
      },

      reportUser: {
        title: "Report a user",
        body: [
          {
            type: "paragraph",
            text: "Reporting helps notify the platform about behavior or content that may violate rules or create safety concerns.",
          },
          {
            type: "bullets",
            items: [
              "Open the user's profile, message, or content menu.",
              "Select Report.",
              "Choose the reason that best describes the issue.",
              "Add details if requested.",
            ],
          },
        ],
      },

myFollowers: {
  title: "My Followers",
  body: [
    {
      type: "paragraph",
      text: "My Followers shows users who follow your profile.",
    },
    {
      type: "bullets",
      items: [
        "Open your profile.",
        "Select Followers.",
        "Review who follows you.",
        "Block or report users if needed.",
      ],
    },
  ],
},

myFollowees: {
  title: "My Followees",
  body: [
    {
      type: "paragraph",
      text: "My Followees shows users whose profiles you follow.",
    },
    {
      type: "bullets",
      items: [
        "Open your profile.",
        "Select Following.",
        "Review the users you follow.",
        "Unfollow users you no longer want to follow.",
      ],
    },
  ],
},

  
///////////////////////////////////////////
/////////Support & Troubleshooting/////////
///////////////////////////////////////////

      contactSupport: {
        title: "Contact support",
        body: [
          {
            type: "paragraph",
            text: "Contact support if you cannot solve an account issue using the available settings or help articles.",
          },
          {
            type: "bullets",
            items: [
              "Describe the issue clearly.",
              "Mention when the problem started.",
              "Include screenshots if useful.",
              "Do not include your password or private verification codes.",
            ],
          },
        ],
      },

      reportIssue: {
        title: "Report a technical issue",
        body: [
          {
            type: "paragraph",
            text: "Technical issues may include broken pages, failed actions, loading problems, or unexpected errors.",
          },
          {
            type: "bullets",
            items: [
              "Describe what you were trying to do.",
              "Mention the device, browser, or app version if known.",
              "Include screenshots or error messages.",
              "Try refreshing or restarting before reporting.",
            ],
          },
        ],
      },

      websiteIssues: {
        title: "Website issues",
        body: [
          {
            type: "paragraph",
            text: "Website issues may be caused by browser settings, network problems, cached files, or temporary service interruptions.",
          },
          {
            type: "bullets",
            items: [
              "Refresh the page.",
              "Clear browser cache if needed.",
              "Try a different browser or private window.",
              "Check your internet connection.",
            ],
          },
        ],
      },

      accountRecovery: {
        title: "Account recovery",
        body: [
          {
            type: "paragraph",
            text: "Account recovery helps you regain access if you cannot log in, lost access to your email, or suspect your account was compromised.",
          },
          {
            type: "bullets",
            items: [
              "Try password reset first.",
              "Use the email connected to your account.",
              "Provide accurate information when contacting support.",
              "Do not create multiple recovery requests unless necessary.",
            ],
          },
        ],
      },

      appealRestriction: {
        title: "Appeal a restriction",
        body: [
          {
            type: "paragraph",
            text: "If your account was restricted, you may be able to appeal the decision depending on the reason and platform rules.",
          },
          {
            type: "bullets",
            items: [
              "Review the restriction notice if available.",
              "Follow the appeal instructions.",
              "Explain why you believe the restriction should be reviewed.",
              "Wait for support or moderation to respond.",
            ],
          },
        ],
      },

      faq: {
        title: "Frequently asked questions",
        body: [
          {
            type: "paragraph",
            text: "Frequently asked questions collect common account issues and quick explanations in one place.",
          },
          {
            type: "bullets",
            items: [
              "Use search or sidebar topics to find the right help page.",
              "Check registration, login, settings, privacy, and account management first.",
              "Contact support if the available help does not solve the issue.",
            ],
          },
        ],
      },
    },
  },
} as const;

export default accountSupport;